import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplatorV2Queries } from '@frontend/api-templator-v2';
import { useTemplateFormShallowStore, useTemplateFormStore } from '@frontend/reviews-landing-template';
import { useAppScopeStore } from '@frontend/scope';
import { TemplateForm } from '@frontend/templates';
import { theme } from '@frontend/theme';
import { Button, Modal, ModalControlResponse } from '@frontend/design-system';
import { TemplateSelectorModal } from './template-selector-modal';

interface NewTemplateFlowProps {
  modalProps: ModalControlResponse['modalProps'];
  onBack: () => void;
  mode: 'edit' | 'create';
  customEditTemplateId?: string;
  currentTab?: string;
}

export const NewTemplateFlow = ({
  modalProps,
  onBack,
  mode,
  customEditTemplateId,
  currentTab,
}: NewTemplateFlowProps) => {
  const { t } = useTranslation('integrated-messaging');
  const { selectedOrgId } = useAppScopeStore();
  const { reset: resetTemplateFormStore } = useTemplateFormStore;
  const { setTemplateFormStateInStore, templateId: storeTemplateId } = useTemplateFormShallowStore(
    'setTemplateFormStateInStore',
    'templateId'
  );
  const { data, isLoading, isError } = TemplatorV2Queries.useTemplate(
    {
      templateId: customEditTemplateId ?? '',
      orgId: selectedOrgId,
    },
    {
      enabled: !!customEditTemplateId && mode === 'edit',
    }
  );

  useEffect(() => {
    if (mode === 'create') resetTemplateFormStore();
  }, [mode, customEditTemplateId, resetTemplateFormStore]);

  useEffect(() => {
    if (!customEditTemplateId || !data?.template || mode !== 'edit') return;

    const template = data?.template;
    if (template?.templateId === customEditTemplateId) {
      setTemplateFormStateInStore({
        activeStep: 1,
        templateId: template?.templateId,
        name: template?.name,
        templateString: template?.templateString,
        templateType: template?.templateTypeSlug,
        locations: template?.businessGroupIds,
      });
    }
  }, [customEditTemplateId, data?.template, setTemplateFormStateInStore]);

  const isReady =
    mode === 'edit'
      ? !isLoading && storeTemplateId === customEditTemplateId
      : storeTemplateId === undefined || storeTemplateId === '';

  const isDataLoading = !isReady && !isError;

  const handleClose = useCallback(() => {
    onBack();
    modalProps.onClose();
  }, [modalProps.onClose, onBack]);

  return (
    <TemplateSelectorModal {...modalProps} isLoading={isDataLoading} css={!isDataLoading && { paddingBottom: '100px' }}>
      <Modal.Header onClose={handleClose} css={{ padding: theme.spacing(1, 4) }}>
        <Button iconName='back' onClick={onBack} variant='secondary' css={{ marginRight: theme.spacing(0.5) }} />
        {t('New Template')}
      </Modal.Header>
      <div css={{ overflow: 'auto', scrollbarWidth: 'none', height: '100%' }}>
        {isReady && (
          <TemplateForm
            key={customEditTemplateId}
            mode={mode}
            templateFormVariant='quick'
            formCompletionHandler={onBack}
            currentTab={currentTab}
          />
        )}
      </div>
    </TemplateSelectorModal>
  );
};
